import { TableInput } from 'models/general';
import { sdk, SCHEMA_NAMES, assertAffectedRecord, checkContainsId } from 'utils/query';
import { ResourceAlreadyExistsError, rqlBuilder } from '@extrahorizon/javascript-sdk';
import { MedicalEventData, MedicalEventDataForm } from 'models/patientMedicalEvent';
import dayjs from 'dayjs';
import { EmptyParametersError } from './errors/EmptyParametersError';
import { EventAlreadyExistsError } from './errors/EventAlreadyExistsError';

export interface GetLogsProps {
  patientId?: string;
  startDate: Date | null;
  endDate: Date | null;
}

export const getLogsTableRecords = async ({ params }: TableInput) => {
  const { patientId } = params;
  const rql = rqlBuilder()
    .eq('data.patientId', patientId || '')
    .build();

  const data = await sdk.data.documents.findAll<MedicalEventData>(SCHEMA_NAMES.patientMedicalEvents, { rql });
  return {
    page: { total: data.length, offset: 0, limit: data.length },
    data,
  };
};

export const getLogs = async ({ patientId }: GetLogsProps) => {
  const rql = rqlBuilder()
    .eq('data.patientId', patientId || '')
    .build();

  return {
    data: await sdk.data.documents.findAll<MedicalEventData>(SCHEMA_NAMES.patientMedicalEvents, {
      rql,
    }),
  };
};

export const getLogById = async (id: string) => {
  const rql = rqlBuilder().eq('id', id).build();
  return {
    data: await sdk.data.documents.findAll<MedicalEventData>(SCHEMA_NAMES.patientMedicalEvents, {
      rql,
    }),
  };
};

export const addLogRecord = async ({
  patientId,
  groupId,
  type,
  loggedTimestamp,
  treatment,
  procedure,
  iteration,
  technology,
  vendor,
  blankingPeriodInWeeks,
  medicationProtocol,
  medicationType,
  diagnosticReason,
  diagnosticMethod,
  diagnosticResult,
  comment,
}: MedicalEventDataForm) => {
  if (!patientId || !groupId || !type || !loggedTimestamp) throw new EmptyParametersError();

  const blankingPeriodValidation = Number(blankingPeriodInWeeks) || undefined;

  try {
    const result = await sdk.data.documents.create(SCHEMA_NAMES.patientMedicalEvents, {
      type,
      loggedTimestamp: dayjs(loggedTimestamp).toISOString(),
      patientId,
      groupId,
      comment,
      treatment: {
        method: treatment,
        iteration,
        procedure: {
          type: procedure,
          technology,
          vendor,
          blankingPeriodInWeeks: blankingPeriodValidation,
        },
        medication: {
          type: medicationType,
          protocol: medicationProtocol,
        },
      },
      diagnosis: {
        reason: diagnosticReason,
        method: diagnosticMethod,
        result: diagnosticResult,
      },
    });
    return checkContainsId({ result });
  } catch (error) {
    if (error instanceof ResourceAlreadyExistsError) {
      return new EventAlreadyExistsError();
    }
    throw error;
  }
};

export const deleteLogRecord = async (variables: { logId?: string }) => {
  const { logId } = variables;
  if (!logId) throw new EmptyParametersError();
  const result = await sdk.data.documents.remove(SCHEMA_NAMES.patientMedicalEvents, logId);
  return assertAffectedRecord({ result });
};
